import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, Chip,
  Tabs, Tab, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import OrderStatusBar from '../components/dc/OrderStatusBar'
import OrderDistributorSummaryCards from '../components/dc/OrderDistributorSummary'
import OrderItemsTable from '../components/dc/OrderItemsTable'
import { dcApi } from '../services/dc.api'

const ORDER_STATUSES = ['draft', 'confirmed', 'ordered', 'partially_received', 'received', 'allocated', 'completed']

export default function DCOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState(0)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [statusDialog, setStatusDialog] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

  const { data, isLoading, error } = useQuery({
    queryKey: ['dc-order', orderId],
    queryFn: () => dcApi.getOrder(parseInt(orderId!)),
    enabled: !!orderId
  })

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => dcApi.updateOrder(parseInt(orderId!), { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dc-order', orderId] })
      setStatusDialog(false)
      setSnackbar({ open: true, message: 'Order status updated' })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: () => dcApi.deleteOrder(parseInt(orderId!)),
    onSuccess: () => {
      navigate('/orders')
    }
  })

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data: updateData }: { itemId: number; data: { order_qty?: number } }) =>
      dcApi.updateOrderItem(parseInt(orderId!), itemId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dc-order', orderId] })
      setSnackbar({ open: true, message: 'Item updated' })
    }
  })

  const handleExportCSV = async (distributorId: number | null) => {
    try {
      const blob = await dcApi.exportOrderCSV(parseInt(orderId!), distributorId || undefined)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      let filename: string
      if (distributorId) {
        const dist = data?.distributorSummary?.find((d) => d.distributor_id === distributorId)
        const distName = (dist?.distributor_name || `distributor_${distributorId}`)
          .toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
        filename = `${distName}.csv`
      } else {
        filename = `${data?.order?.order_number || `order_${orderId}`}_all.csv`
      }
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to export CSV' })
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load order: {(error as Error)?.message || 'Not found'}</Alert>
  }

  const { order, items, distributorSummary } = data
  const isDraft = order.status === 'draft'

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
            Back
          </Button>
          <Typography variant="h5">{order.order_number}</Typography>
          <Chip label={order.status} size="small" color={isDraft ? 'default' : 'primary'} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => { setNewStatus(order.status); setStatusDialog(true) }}
          >
            Change Status
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExportCSV(null)}
          >
            Export All CSV
          </Button>
          {isDraft && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirm(true)}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Status Bar */}
      <OrderStatusBar status={order.status} />

      {/* Order Summary */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="body2">
          <strong>Date:</strong> {order.order_date || '-'}
        </Typography>
        <Typography variant="body2">
          <strong>Items:</strong> {order.total_items}
        </Typography>
        <Typography variant="body2">
          <strong>Distributors:</strong> {order.total_distributors}
        </Typography>
        <Typography variant="body2">
          <strong>Est. Cost:</strong> Rs. {(order.total_estimated_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </Typography>
        {order.notes && (
          <Typography variant="body2">
            <strong>Notes:</strong> {order.notes}
          </Typography>
        )}
      </Box>

      {/* Distributor Summary Cards */}
      <Typography variant="h6" gutterBottom>By Distributor</Typography>
      <Box sx={{ mb: 3 }}>
        <OrderDistributorSummaryCards
          distributors={distributorSummary}
          onExportCSV={handleExportCSV}
        />
      </Box>

      {/* Tabs: All Items / Per Distributor */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All Items (${items.length})`} />
        {distributorSummary.map((d) => (
          <Tab key={d.distributor_id} label={`${d.distributor_name || 'Unknown'} (${d.item_count})`} />
        ))}
      </Tabs>

      <OrderItemsTable
        items={items}
        editable={isDraft}
        onUpdateItem={(itemId, updateData) => updateItemMutation.mutate({ itemId, data: updateData })}
        filterDistributorId={activeTab > 0 ? distributorSummary[activeTab - 1]?.distributor_id : undefined}
      />

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Change Order Status</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} label="Status">
              {ORDER_STATUSES.map(s => (
                <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateStatusMutation.mutate(newStatus)}
            disabled={updateStatusMutation.isPending}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Order?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {order.order_number}? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  )
}
